import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import BasePayRangeSummaryView from 'nomst/Components/SummaryDashboard/basePayRangeSummaryView';
import TeamWorkflowSummaryRow from 'nomst/Components/SummaryDashboard/teamWorkflowSummaryRow';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import TeamProgressSummaryNotReadyView from 'nomst/Components/SummaryDashboard/teamProgressSummaryNotReadyView';
import PromotionRequestSummaryView from 'nomst/Components/SummaryDashboard/promotionRequestSummaryView';
import CycleClosingAlert from 'nomst/Components/Decision/cycleClosingAlert';
import CycleClosedAlert from 'nomst/Components/Decision/cycleClosedAlert';
import { PreviewCycleAlert } from 'nomst/Components/Decision/previewCycleAlert';
import { CyclePublishedAlert } from 'nomst/Components/Decision/cyclePublishedAlert';
import { InvalidEmployeeDataAlert } from 'nomst/Components/Decision/invalidEmployeeDataAlert';
import { CountryBudgetCard } from 'nomst/Components/Decision/countryBudgetCard';
import { CountryBudget } from 'nomst/Stores/Decision/countryBudget';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { formatDate, dateTextFormat } from 'utils/dateUtils';
import { configurationsStore, cycleRules, teamErrorData } from 'utils/MobxDataManager/singletons';
import { navigateToTeamDataSummary } from 'nomst/Actions/configurations';
import { featureFlagHelper } from 'singletons';
import { AmountCode, FeatureFlags } from 'typings/enums';
import './summaryDashboardView.scss';

export interface ISummaryDashboardViewProps {
    store: MobxTeamStore;
}

export class SummaryDashboardView extends React.Component<ISummaryDashboardViewProps> {

    componentDidMount(): Promise<void> {
        const { summaryDashboardStore } = this.props.store;
        return summaryDashboardStore.fetchTeamOverviewSummary();
    }

    render(): React.ReactNode {
        const { user, summaryDashboardStore } = this.props.store;
        const configuration = configurationsStore.currentConvertedConfiguration;
        const showErrorBanner = featureFlagHelper.featureFlagSettings[FeatureFlags.ShowDataSummary]
            && featureFlagHelper.featureFlagSettings[FeatureFlags.ShowDataErrorBanner]
            &&  teamErrorData.showInvalidEmployeeAlert;

        return (
            <div className="nomst-dashboard">
                <PreviewCycleAlert />
                <CycleClosingAlert />
                <CycleClosedAlert
                    hasHrOrSupportRole={user.isHrOrSupport}
                    configuration={configuration}
                />
                <CyclePublishedAlert
                    hasHrOrSupportRole={user.isHrOrSupport}
                    exportAllManagersToCsv={summaryDashboardStore.exportAllManagersToCsv}
                    configuration={configuration}
                />
                {showErrorBanner &&
                    <InvalidEmployeeDataAlert
                        onNavClick={navigateToTeamDataSummary}
                    />
                }
                <div className="nomst-dashboard__summary-container">
                    <div className="nomst-dashboard__summary">
                        <div className="nomst-dashboard__header">
                            <h1 className="nomst-dashboard__heading">
                                Team Summary
                            </h1>
                            <div className="nomst-dashboard__cycle-dates">
                                <p className="nomst-dashboard__cycle-info nomst-dashboard__cycle-info--start-date">
                                    Cycle Started On
                                    <span className="nomst-dashboard__cycle-date">
                                        {this.renderCycleStartedOnText(configuration)}
                                    </span>
                                </p>
                                <p className="nomst-dashboard__cycle-info nomst-dashboard__cycle-info--effective-date">
                                    Pay Effective Date
                                    <span className="nomst-dashboard__cycle-date">
                                        {this.renderPayEffectiveDateText(configuration)}
                                    </span>
                                </p>
                            </div>
                        </div>
                        {this.renderTeamSummaryView()}
                    </div>
                </div>
                <div className="nomst-dashboard__additional-boxes">
                    {this.renderBasePayRangeSummary()}
                    {this.renderPromotionRequestSummary()}
                </div>
            </div>
        );
    }

    renderTeamSummaryView(): React.ReactNode {
        const { store } = this.props;

        if (!cycleRules.hasCycleOpenedNotPublished) {
            return (
                <div className="nomst-dashboard__body nomst-dashboard__body--not-ready">
                    <TeamProgressSummaryNotReadyView store={store} />
                </div>
            );
        }

        const { summaryDashboardStore } = store;
        const teamWorkflowSummaries = summaryDashboardStore.overviewSummary.workflowSummary || [];
        const progressRows = teamWorkflowSummaries.map(teamWorkflowSummary =>
            (
                <TeamWorkflowSummaryRow
                    key={teamWorkflowSummary.status}
                    teamWorkflowSummary={teamWorkflowSummary}
                    summaryDashboardStore={summaryDashboardStore}
                />
            )
        );

        return (
            <div className="nomst-dashboard__body">
                <div className="nomst-dashboard__budget-summary">
                    <h2 className="nomst-dashboard__subheading">
                        Budgets
                    </h2>
                    <div className="nomst-dashboard__budgets">
                        {this.renderBudgetStatistics()}
                    </div>
                </div>
                <div className="nomst-dashboard__team-summary">
                    <h2 className="nomst-dashboard__subheading">
                        Team Progress
                    </h2>
                    {progressRows}
                </div>
            </div>
        );
    }

    renderBasePayRangeSummary(): React.ReactNode {
        const { summaryDashboardStore } = this.props.store;
        const configuration = configurationsStore.currentConvertedConfiguration;
        const basePayRangeSummaries = summaryDashboardStore.overviewSummary.basePayRangeSummary;
        if (!cycleRules.hasCycleOpenedNotPublished ||
            !configuration.payRangeConfiguration.showPayRange ||
            !basePayRangeSummaries
        ) {
            return null;
        }

        return (
            <BasePayRangeSummaryView
                basePayRangeSummaries={basePayRangeSummaries}
            />
        );
    }

    renderPromotionRequestSummary(): React.ReactNode {
        const { summaryDashboardStore } = this.props.store;
        const configuration = configurationsStore.currentConvertedConfiguration;
        if (!cycleRules.hasCycleOpenedNotPublished) {
            return null;
        }

        const hasPromotionBudget = configuration.doesBudgetForAmountCodeExist(AmountCode.Promotion);
        const promotions = summaryDashboardStore.overviewSummary.promotionRequestSummary;
        if (!hasPromotionBudget || !promotions) {
            return null;
        }

        return (
            <PromotionRequestSummaryView
                promotionRequestSummaries={promotions}
            />
        );
    }

    renderBudgetStatistics(): React.ReactNode {
        const { summaryDashboardStore } = this.props.store;
        const configuration = configurationsStore.currentConvertedConfiguration || {} as Configuration;
        const budgets = summaryDashboardStore.overviewSummary.budgetStatistics;
        const managersOverBudget = summaryDashboardStore.overviewSummary.managerOverBudgetSummaries;
        if (!cycleRules.hasCycleOpenedNotPublished || !budgets) {
            return null;
        }

        const activeBudgets: IBudgetSummary[] = [];
        budgets.forEach(budget => {
            const cycleBudget = configuration.cycleBudgets.find(cb => cb.budgetCycleId === budget.budgetCycleId);
            if (cycleBudget?.associatedFields?.length) {
                // a budget is active if it has at least one associated field
                activeBudgets.push(budget);
            }
        });

        // only show active budgets on the dashboard
        return activeBudgets.map((budget) => {
            const countryBudget = new CountryBudget({
                budgetCycleId: budget.budgetCycleId,
                name: budget.name,
                totalAmount: budget.budgetAmount || 0,
                totalCurrentPay: budget.totalConvertedAnnualCurrentPay || 0,
                totalSpent: budget.decisionAmount || 0,
                isStockBudget: !!budget.isStockBudget,
                isStockInUnitOnly: !!budget.isStockInUnitOnly,
                allowManagerOverBudget: false,
                requireCommentWhenOverBudget: false
            } as ICountryBudget,
            configuration.homeCountryCurrency,
            new CurrencyConversion(configuration.currencies));

            const overBudget = managersOverBudget?.find(mob => mob.budgetCycleId === budget.budgetCycleId);
            const overBudgetCount = overBudget?.overBudgetManagers;
            return (
                <CountryBudgetCard
                    key={budget.budgetCycleId}
                    countryBudget={countryBudget}
                    configuration={configuration}
                    overBudgetCount={overBudgetCount}
                />
            );
        });
    }

    renderCycleStartedOnText(configuration: Configuration): string {
        if (!cycleRules.hasCycleOpenedNotPublished || !configuration.startDate) {
            return 'Not Opened';
        }
        return formatDate(configuration.startDate, dateTextFormat);
    }

    renderPayEffectiveDateText(configuration: Configuration): string {
        if (!cycleRules.hasCycleOpenedNotPublished || !configuration.payEffectiveDate) {
            return 'Not Available';
        }
        return formatDate(configuration.payEffectiveDate, dateTextFormat);
    }
}

export default inject('store')(observer(SummaryDashboardView));